/**
 * Composable for accessing Sezzle configuration and eligibility checks.
 *
 * Reads Sezzle integration settings from `public.sezzle` in the Nuxt runtime config
 * and provides helpers to determine whether Sezzle should be displayed for a given amount.
 */
export const useSezzle = () => {
  const { enabled, minAmount, maxAmount } = useRuntimeConfig().public.sezzle;

  /**
   * Checks if an amount falls within the configured min/max bounds.
   *
   * @param amount - The order amount to validate.
   * @returns true if the amount is >= minAmount and <= maxAmount.
   */
  const valid = (amount: number) => amount >= minAmount && amount <= maxAmount;

  /**
   * Checks if Sezzle is globally enabled *and* the given amount is valid.
   *
   * @param amount - The order amount to test.
   * @returns true if Sezzle is enabled and the amount is within the allowed range.
   */
  const available = (amount: number) => enabled && valid(amount);

  return {
    /**
     * Global flag to toggle Sezzle integration.
     */
    enabled,
    /**
     * Minimum order amount (inclusive) that qualifies for Sezzle.
     */
    minAmount,
    /**
     * Maximum order amount (inclusive) that qualifies for Sezzle.
     */
    maxAmount,
    /**
     * Validates whether a given amount falls within the Sezzle min/max limits.
     */
    valid,
    /**
     * Determines whether Sezzle should be offered for a given amount.
     * Combines the `enabled` flag and the `valid` range check.
     */
    available,
  };
};
